import { GoogleAnalytics } from "~/google_analytics/events";
import { EventParameter } from "~/google_analytics/events.enum";
import { setLocationByAddressHandler } from "~/services/location/usecases/setLocationByAddress";
import { setLocationByCoordinatesHandler } from "~/services/location/usecases/setLocationByCoordinates";
import type { Address } from "~/types/address";
import { formatAddressWithPincode } from "~/utility/location/address.formatter";
import { mapDeliveryAddress, mapGeoResponse } from "~/utility/location/location.mapper";
import { addRecentAddress, getRecentAddresses } from "~/utility/location/recent.location.storage";

const isValidAddress = (address: Address | null) => {
    return !!(
        address &&
        address.gps?.latitude !== undefined &&
        address.gps?.longitude !== undefined &&
        (address.city?.name || address.city) &&
        address.area_code
    );
};

export const useGeoLocation = defineStore('geolocation', {

    state: () => ({
        selectedAddress: null as Address | null,
        deliveryAddress: null as Address | null,
        addOrEditAddress: null as any | null,
        isInitialized: false,
    }),

    getters: {
        getSelectedAddress: (state) => {
            if (state.selectedAddress) return state.selectedAddress;
            return null;
        },

        formatSelectedAddress(): ReturnType<typeof formatAddressWithPincode> {
            return formatAddressWithPincode(this.getSelectedAddress);
        },

        isValidLocation: (state) => {
            return isValidAddress(state.selectedAddress);
        },

        isValidDeliveryAddress: (state) => {
            return isValidAddress(state.deliveryAddress);
        }

    },

    actions: {

        // ✅ INIT (VERY IMPORTANT)
        init() {
            if (process.client && !this.isInitialized) {
                const stored = getRecentAddresses();

                if (stored?.length) {
                    // optional: restore selectedAddress
                    if (!this.selectedAddress) {
                        this.selectedAddress = stored[0];
                    }
                }

                this.isInitialized = true;
            }
        },

        async setLocationByCoordinates(lat: number, lng: number) {
            if (!lat || !lng) return;
            const result = await setLocationByCoordinatesHandler(lat, lng);
            if (!result.success) return result;

            const data = result.data as Address;

            if (!isValidAddress(data)) {
                return {
                    success: false,
                    message: "This location seems incomplete. Please choose another or a nearby location."
                };
            }

            // ✅ update recent (localStorage + state sync)
            addRecentAddress(data as Address);

            this.setSelectedAddress(data as Address);

            this.deliveryAddress = null;

            return result;
        },

        async setLocationByAddress(address: string) {
            if (!address) return;

            const result = await setLocationByAddressHandler(address);

            if (!result.success) return result;

            const data = result.data as Address;

            if (!isValidAddress(data)) {
                return {
                    success: false,
                    message: "This location seems incomplete. Please choose another or a nearby location."
                };
            }

            addRecentAddress(data as Address);

            this.setSelectedAddress(data as Address);

            this.deliveryAddress = null;

            return result;
        },

        async setSelectedAddress(address: Address) {
            if (!address) return;
            this.selectedAddress = address;
            await this.sendGALocationEvent();
        },

        async setAddOrEditAddress(address: any, editAddress: boolean = false, setDeliveryAddress: boolean = false) {
            if (!address) return;
            this.addOrEditAddress = address;
            if (setDeliveryAddress) {
                this.setDeliveryAddress(address as any);
            } else if (!editAddress) {
                const mappedResponse = await mapGeoResponse(address, "USER_ADDRESS");
                this.setSelectedAddress(mappedResponse);
            }
        },

        async setDeliveryAddress(address: Address | any) {
            if (!address) return;
            const selectedAddress = await mapGeoResponse(address, "USER_ADDRESS");
            this.setSelectedAddress(selectedAddress);
            const deliveryAddress = await mapDeliveryAddress(address);
            const checkout = useCheckout();
            checkout.setDeliveryAddress(deliveryAddress);
        },

        async setOnlyDeliveryAddress(address: any) {
            this.deliveryAddress = address;
        },

        async sendGALocationEvent() {
            GoogleAnalytics.locationEvent({
                [EventParameter.ACTION]: 'location_updated',
                [EventParameter.LATITUDE]: this.selectedAddress?.gps?.latitude || 0,
                [EventParameter.LONGITUDE]: this.selectedAddress?.gps?.longitude || 0,
                [EventParameter.ADDRESS]: this.formatSelectedAddress || 'none',
                [EventParameter.REASON]: 'none',
                [EventParameter.LOCATION_AVAILABLE]: this.selectedAddress?.gps ? "yes" : "no",
                [EventParameter.PROMPT_TYPE]: "none",
                [EventParameter.NEEDS_LOCATION_FETCH]: this.selectedAddress?.gps ? "no" : "yes",
            });
        },

    },

    persist: {
        storage: piniaPluginPersistedstate.localStorage(),
    },
});